import { type FC, useState } from 'react'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { PastDuePolicy, ReminderType, type ReminderSettings } from '../../../../models/Scheduling'
import { useGetReminderSettingsQuery, usePreviewReminderSettingsMutation, usePutReminderSettingsMutation } from '../../../../store/scheduling/scheduling'
import { useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { ConfirmDialog } from '../../../Scheduling/shared/ConfirmDialog'
import { SettingsSection } from './SettingsSection'
import { ReminderSettingsCard, type ReminderSettingsCopy, type SmsChannelState } from './ReminderSettingsCard'
import { PastDueConfirmModal } from './PastDueConfirmModal'
import { hasEntryMovedEarlier, toSettingsDraft, toSettingsUpdate, withoutSmsChannel, type ReminderSettingsDraft } from './reminderDrafts'
import { type PendingConfirm, reminderSaveEvents, SAVE_ERROR_MESSAGE, TURN_OFF_MODAL_COPY } from './Reminders/reminderSaveEvents'

const FOLLOW_UP_COPY: ReminderSettingsCopy = {
  title: 'Booking follow-ups',
  heading: "Follow up when a client hasn't booked",
  description: "Remind clients who received a booking link but haven't booked yet. Follow-ups stop once they book.",
  banner: "Follow-ups aren't sent for copied emails, links or emails saved as drafts.",
  rowLabel: 'Send follow-up',
  trailingCopy: 'after the invite is sent.',
  addLabel: 'Add follow-up',
  removeLabel: 'Remove follow-up',
  removeLastTooltip: 'Turn follow-ups off to stop sending them',
}

const MEETING_REMINDERS_COPY: ReminderSettingsCopy = {
  title: 'Meeting reminders',
  heading: 'Send reminders before meetings',
  description: 'Remind clients about an upcoming meeting.',
  rowLabel: 'Send reminder',
  trailingCopy: 'before the meeting.',
  channelsTrailingCopy: 'before the meeting by',
  addLabel: 'Add reminder',
  removeLabel: 'Remove reminder',
  removeLastTooltip: 'Turn reminders off to stop sending them',
}

export interface RemindersSectionProps {
  readOnly: boolean
  smsChannel: SmsChannelState
  templatesLabel?: string
  onShowMessageTemplates: () => void
  onShowTextMessages: () => void
}

export const LegacyRemindersSection: FC<RemindersSectionProps> = ({ readOnly, smsChannel, templatesLabel, onShowMessageTemplates, onShowTextMessages }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data, isLoading, isError } = useGetReminderSettingsQuery({ accountId })
  const [editingType, setEditingType] = useState<ReminderType | null>(null)
  const [draft, setDraft] = useState<ReminderSettingsDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)
  const [putReminderSettings, { isLoading: isSaving }] = usePutReminderSettingsMutation()
  const [previewReminderSettings, { isLoading: isPreviewing }] = usePreviewReminderSettingsMutation()
  const { trackingClient } = useTracking()

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  const followUp = data?.data.find((settings) => settings.reminderType === ReminderType.FOLLOW_UP)
  const savedMeetingReminders = data?.data.find((settings) => settings.reminderType === ReminderType.MEETING)
  const meetingReminders = savedMeetingReminders != null && smsChannel === 'hidden' ? withoutSmsChannel(savedMeetingReminders) : savedMeetingReminders

  if (isError || data == null || followUp == null || meetingReminders == null) {
    return <div className='paragraph-small zocks-text-gray-600'>Couldn't load reminder settings. Please refresh to try again.</div>
  }

  const startEditing = (settings: ReminderSettings) => {
    setError(null)
    setEditingType(settings.reminderType)
    setDraft(toSettingsDraft(smsChannel === 'off' && settings.reminderType === ReminderType.MEETING ? withoutSmsChannel(settings) : settings))
    setPendingConfirm(null)
  }

  const cancelEditing = () => {
    setError(null)
    setEditingType(null)
    setDraft(null)
    setPendingConfirm(null)
  }

  const handleSave = async (pastDuePolicy: PastDuePolicy = PastDuePolicy.SEND) => {
    if (draft == null || editingType == null) {
      return
    }
    const settings = editingType === ReminderType.FOLLOW_UP ? followUp : meetingReminders
    const pastDueCount = pendingConfirm?.kind === 'past-due' ? pendingConfirm.pastDueCount : undefined
    try {
      await putReminderSettings({ accountId, reminderType: editingType, update: toSettingsUpdate(draft, pastDuePolicy) }).unwrap()
      const saveEvents = reminderSaveEvents({ reminderType: editingType, draft, settings, pastDuePolicy, pastDueCount })
      saveEvents.forEach((event) => trackingClient.track(event))
      setError(null)
      setEditingType(null)
      setDraft(null)
    } catch {
      setError(SAVE_ERROR_MESSAGE)
    } finally {
      setPendingConfirm(null)
    }
  }

  const requestSave = async () => {
    if (draft == null || editingType == null) {
      return
    }
    const settings = editingType === ReminderType.FOLLOW_UP ? followUp : meetingReminders
    if (settings.enabled && !draft.enabled) {
      setPendingConfirm({ kind: 'turn-off' })
      return
    }
    if (!draft.enabled || !hasEntryMovedEarlier(draft, settings)) {
      await handleSave()
      return
    }
    const pastDueCount = await previewReminderSettings({ accountId, reminderType: editingType, update: toSettingsUpdate(draft, PastDuePolicy.SEND) })
      .unwrap()
      .then((preview) => preview.pastDueCount)
      .catch(() => 0)
    if (pastDueCount > 0) {
      setPendingConfirm({ kind: 'past-due', pastDueCount })
    } else {
      await handleSave()
    }
  }

  const editDisabledTooltip = editingType === ReminderType.FOLLOW_UP ? 'Finish editing booking follow-ups first' : 'Finish editing meeting reminders first'

  const isSavingOrPreviewing = isSaving || isPreviewing

  return (
    <SettingsSection
      title='Reminders'
      subtitle='Reminders for meetings booked through Scheduling. These apply to all event types in the account.'
      isSaving={isSavingOrPreviewing}
    >
      <ReminderSettingsCard
        copy={FOLLOW_UP_COPY}
        settings={followUp}
        draft={editingType === ReminderType.FOLLOW_UP ? draft : null}
        smsChannel='hidden'
        templatesLabel={templatesLabel}
        readOnly={readOnly}
        editDisabled={editingType != null && editingType !== ReminderType.FOLLOW_UP}
        editDisabledTooltip={editDisabledTooltip}
        isSaving={isSavingOrPreviewing}
        error={editingType === ReminderType.FOLLOW_UP ? error : null}
        onEdit={() => startEditing(followUp)}
        onCancel={cancelEditing}
        onSave={requestSave}
        onDraftChange={setDraft}
        onShowMessageTemplates={onShowMessageTemplates}
        onShowTextMessages={onShowTextMessages}
      />
      <ReminderSettingsCard
        copy={MEETING_REMINDERS_COPY}
        settings={meetingReminders}
        draft={editingType === ReminderType.MEETING ? draft : null}
        smsChannel={smsChannel}
        templatesLabel={templatesLabel}
        readOnly={readOnly}
        editDisabled={editingType != null && editingType !== ReminderType.MEETING}
        editDisabledTooltip={editDisabledTooltip}
        isSaving={isSavingOrPreviewing}
        error={editingType === ReminderType.MEETING ? error : null}
        onEdit={() => startEditing(meetingReminders)}
        onCancel={cancelEditing}
        onSave={requestSave}
        onDraftChange={setDraft}
        onShowMessageTemplates={onShowMessageTemplates}
        onShowTextMessages={onShowTextMessages}
      />
      {pendingConfirm?.kind === 'turn-off' && editingType != null ? (
        <ConfirmDialog
          title={TURN_OFF_MODAL_COPY[editingType].title}
          body={TURN_OFF_MODAL_COPY[editingType].body}
          confirmLabel='Turn off'
          confirmVariant='primary'
          isLoading={isSaving}
          onConfirm={handleSave}
          onCancel={() => setPendingConfirm(null)}
        />
      ) : null}
      {pendingConfirm?.kind === 'past-due' && editingType != null ? (
        <PastDueConfirmModal
          reminderType={editingType}
          pastDueCount={pendingConfirm.pastDueCount}
          isSaving={isSavingOrPreviewing}
          onConfirm={handleSave}
          onCancel={() => setPendingConfirm(null)}
        />
      ) : null}
    </SettingsSection>
  )
}
